import React, { createContext, useContext, useCallback, useState } from 'react';
import { normalizeIANATimeZone, normalizeTimeZone } from '@chronosphereio/core';
import { useQueryParam, StringParam } from 'use-query-params';
import { useDeepMemo } from '@/core';

export interface TimeZone {
  /**
   * The time zone configured in the user's account settings.
   * It should be in the IANA standard format (e.g. "America/New_York").
   * A list of all the IANA timezones can be found here: https://timezonedb.com/time-zones.
   */
  userTimeZone: string;
  /**
   * The time zone used for formatting start and end dates.
   * - Defaults to `initialTimeZone ?? userTimeZone` unless it is overridden.
   * - If overridden, this returns the selected time zone instead of the default.
   * - This should be used in most cases and should be considered as the single source of truth for time zone usage.
   *
   * The framework manages time zone overrides internally.
   * This also follows the IANA standard format (e.g. "America/New_York").
   */
  timeZone: string;
  /**
   * Callback to override the user time zone.
   * - When URL params are enabled: appends `?timeZone=` as a query param in the url.
   * - When URL params are disabled: updates the in-memory time zone state.
   */
  setTimeZone?: (timeZone: string) => void;
}

export const TimeZoneContext = createContext<TimeZone | undefined>(undefined);

export interface TimeZoneProviderProps {
  /**
   * The time zone configured in the user's account settings.
   */
  userTimeZone: string;
  /**
   * The initial time zone to use. Defaults to `userTimeZone` if not provided.
   * This allows pages to start with a specific timezone (e.g., UTC) while still
   * tracking the user's actual account preference in `userTimeZone`.
   */
  initialTimeZone?: string;
  /**
   * When true, uses React state instead of URL query params for managing time zone.
   */
  disableURLParams?: boolean;
  children?: React.ReactNode;
}

export function TimeZoneProvider(props: TimeZoneProviderProps) {
  const { children, userTimeZone, initialTimeZone, disableURLParams } = props;

  const defaultTimeZone = initialTimeZone ?? userTimeZone;
  const [timeZoneQueryParam, setTimeZoneQueryParam] = useQueryParam('timeZone', StringParam);
  const [timeZoneState, setTimeZoneState] = useState<string>(defaultTimeZone);

  let timeZone = defaultTimeZone;
  if (disableURLParams) {
    timeZone = timeZoneState;
  } else if (timeZoneQueryParam) {
    timeZone = timeZoneQueryParam;
  }

  const setTimeZone = useCallback(
    (newTimeZone: string) => {
      const normalizedNewTimeZone = normalizeIANATimeZone(newTimeZone);
      const normalizedDefaultTimeZone = normalizeIANATimeZone(defaultTimeZone);
      if (disableURLParams) {
        setTimeZoneState(newTimeZone);
      } else {
        if (normalizedNewTimeZone === normalizedDefaultTimeZone) {
          setTimeZoneQueryParam(null);
        } else {
          setTimeZoneQueryParam(newTimeZone);
        }
      }
    },
    [disableURLParams, setTimeZoneQueryParam, defaultTimeZone]
  );

  // We use useDeepMemo to prevent this from re-running on every query param change.
  // There should be no performace implication as the dependency array is small.
  const ctx = useDeepMemo(() => {
    return { userTimeZone, timeZone, setTimeZone };
  }, [userTimeZone, timeZone, setTimeZone]);

  return <TimeZoneContext.Provider value={ctx}>{children}</TimeZoneContext.Provider>;
}

export function useTimeZone() {
  const ctx = useContext(TimeZoneContext);
  return {
    userTimeZone: normalizeTimeZone(ctx?.userTimeZone),
    // If TimeZoneProvider is missing from the React tree, default to the account time zone first, followed by the local time zone.
    timeZone: normalizeTimeZone(ctx?.timeZone ?? ctx?.userTimeZone),
    setTimeZone: ctx?.setTimeZone,
  };
}
